import { ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik, FormikProvider, FieldArray } from 'formik';
import {
  Button,
  Grid,
  H2,
  Paragraph,
  Checkbox,
  DateInput,
} from '@increasecard/typed-components';
import { InputWithDescription } from '../../components/common/InputWithDescription';
import { InputCurrency } from '../../components/common/InputCurrency';
import { ScreenSeparator } from '../../components/common/ScreenSeparator';
import { Row } from '../../components/common/Row';
import {
  InputTextField,
  SelectField,
} from '../../components/common/FormikFields';
import { DeleteButton } from '../../components/common/DeleteButton';
import {
  AdditionalField,
  PaymentLink,
  PaymentLinkFormValues,
} from '../../types/PaymentLink';
import { PaymentLinkValidationSchema } from './PaymentLinkValidationSchema';
import { useCreatePaymentLink } from './paymentLinkQueries';
import styled from 'styled-components';

interface PaymentLinkFormProps {
  onSuccess: (link: PaymentLink) => void;
  onCancel?: VoidFunction;
}

const VALIDATION_TYPES = [
  { value: 'numeric', name: 'Numérico' },
  { value: 'alphabetic', name: 'Alfabético' },
  { value: 'alphanumeric', name: 'Alfanumérico' },
];

function createEmptyField(): AdditionalField {
  return {
    id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    label: '',
    required: false,
    validation_type: 'alphanumeric',
    max_length: undefined,
  };
}

const AdditionalFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto auto;
  gap: 8px;
  align-items: start;
  max-width: 760px;
  .required-checkbox {
    margin-top: 6px;
  }
`;

export function PaymentLinkForm({
  onSuccess,
  onCancel,
}: PaymentLinkFormProps): JSX.Element {
  const history = useHistory();
  const { mutate, isLoading, error } = useCreatePaymentLink();

  const formik = useFormik<PaymentLinkFormValues>({
    initialValues: {
      name: '',
      amount: '',
      expiration_date: '',
      success_url: '',
      return_url: '',
      additional_fields: [],
    },
    validationSchema: PaymentLinkValidationSchema,
    onSubmit: (values) => {
      console.log(values);
      mutate(values, {
        onSuccess: (data) => {
          onSuccess(data);
        },
      });
    },
  });

  const { values, errors, touched, handleSubmit, setFieldValue } = formik;

  const handleCancel = () => (onCancel ? onCancel() : history.goBack());

  return (
    <FormikProvider value={formik}>
      <Grid as="form" rowGap={2} onSubmit={handleSubmit}>
        <InputWithDescription
          title="Nombre del link"
          value={values.name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setFieldValue('name', e.target.value)
          }
          description="Un nombre descriptivo para identificar el link"
        />

        <ScreenSeparator margin="24px 0" />

        <H2>Monto</H2>
        <InputCurrency
          value={Number(values.amount) || 0}
          onChange={(val) => setFieldValue('amount', val.toString())}
          placeholder="0"
        />

        <ScreenSeparator margin="24px 0" />

        <H2>Fecha de expiración</H2>
        <DateInput
          id="expiration_date"
          value={
            values.expiration_date
              ? new Date(values.expiration_date)
              : undefined
          }
          onChange={(date: Date) =>
            setFieldValue('expiration_date', date.toISOString())
          }
          datepickerProps={{ minDate: new Date() }}
          errorMessage={touched.expiration_date && errors.expiration_date}
        />

        <ScreenSeparator margin="24px 0" />

        <InputTextField
          id="success_url"
          label="URL de éxito"
          placeholder="https://..."
          description="URL a la que se redirige al pagador después de un pago exitoso"
        />

        <ScreenSeparator margin="24px 0" />

        <InputTextField
          id="return_url"
          label="URL de retorno"
          placeholder="https://..."
          description="URL a la que se redirige al pagador si cancela o vuelve atrás"
        />

        <ScreenSeparator margin="24px 0" />

        <H2>Campos adicionales</H2>
        <FieldArray name="additional_fields">
          {({ push, remove }) => (
            <>
              {(values.additional_fields ?? []).map((_, index) => (
                <AdditionalFieldGrid key={index}>
                  <InputTextField
                    id={`additional_fields.${index}.label`}
                    placeholder="Label del campo"
                    size="small"
                    labelHidden
                  />
                  <SelectField
                    id={`additional_fields.${index}.validation_type`}
                    options={VALIDATION_TYPES}
                    size="small"
                    label=""
                    labelHidden
                  />
                  <InputTextField
                    id={`additional_fields.${index}.max_length`}
                    placeholder="Largo máx."
                    type="number"
                    size="small"
                    labelHidden
                  />
                  <Checkbox
                    id={`additional_fields.${index}.required`}
                    label="Requerido"
                    size="small"
                    className="required-checkbox"
                    checked={
                      values.additional_fields
                        ? values.additional_fields[index].required
                        : false
                    }
                    onChange={(e) =>
                      setFieldValue(
                        `additional_fields.${index}.required`,
                        e.target.checked
                      )
                    }
                  />
                  <DeleteButton onClick={() => remove(index)} />
                </AdditionalFieldGrid>
              ))}
              <Button
                variant="invisible"
                type="button"
                onClick={() => push(createEmptyField())}
                disabled={(values.additional_fields?.length ?? 0) >= 5}
              >
                + Agregar campo
              </Button>
            </>
          )}
        </FieldArray>

        <ScreenSeparator margin="40px 0" />

        {error && (
          <Paragraph style={{ color: 'var(--color-coral-regular)' }}>
            Error al crear el link de pago
          </Paragraph>
        )}

        <Row marginTop="2rem" flexGap="1rem">
          <Button
            type="submit"
            disabled={
              isLoading ||
              !values.name ||
              !values.amount ||
              !values.expiration_date
            }
          >
            {isLoading ? 'Creando...' : 'Crear link de pago'}
          </Button>
          <Button type="button" buttonType="invisible" onClick={handleCancel}>
            Cancelar
          </Button>
        </Row>
      </Grid>
    </FormikProvider>
  );
}
